import type { GbTier } from "@/lib/pricing-utils";

export const PRICING_VERIFIED_DATE = "August 12, 2026";

export const DATAIMPULSE_URL = "https://dataimpulse.com";
export const DATAIMPULSE_MOBILE_URL = "https://dataimpulse.com/mobile-proxies/";
export const DATAIMPULSE_RESIDENTIAL_URL = "https://dataimpulse.com/residential-proxies/";
export const DATAIMPULSE_PRICING_URL = "https://dataimpulse.com/pricing/";
export const DATAIMPULSE_TARGETING_DOCS_URL = "https://docs.dataimpulse.com/proxies/targeting";

// Confirmed on DataImpulse's own targeting docs: "Traffic routed through the Target Filter
// (state, city, ZIP code, and ASN) is billed at double the standard rate." Country selection/
// exclusion and ASN exclusion stay at the standard rate — only selecting a specific state, city,
// ZIP, or ASN triggers the multiplier. Verified alongside the rest of this page's pricing data.
export const DATAIMPULSE_TARGETING_MULTIPLIER = 2;

export type ProxyProduct = "mobile" | "residential";

export interface DataImpulseRateTier {
  label: string;
  minGb: number;
  perGb: number;
  refPrice: number;
  refGb: number;
}

// DataImpulse bills pay-as-you-go per GB; the rate drops once monthly usage
// crosses a published breakpoint. Their 1 TB "Advanced" package and their
// 5 TB+ "Custom+" starting price work out to the same per-GB rate, so there
// are effectively two real rates per product below custom-quote territory,
// not four separate "plans."
export const DATAIMPULSE_MOBILE_TIERS: DataImpulseRateTier[] = [
  { label: "Intro/Basic rate", minGb: 0, perGb: 2, refPrice: 50, refGb: 25 },
  { label: "Advanced rate", minGb: 1000, perGb: 1.6, refPrice: 1600, refGb: 1000 },
];

export const DATAIMPULSE_RESIDENTIAL_TIERS: DataImpulseRateTier[] = [
  { label: "Intro/Basic rate", minGb: 0, perGb: 1, refPrice: 50, refGb: 50 },
  { label: "Advanced rate", minGb: 1000, perGb: 0.8, refPrice: 800, refGb: 1000 },
];

export const DATAIMPULSE_MOBILE_MINIMUM = { price: 5, gb: 2.5 };
export const DATAIMPULSE_RESIDENTIAL_MINIMUM = { price: 5, gb: 5 };

export const PROXIDIZE_MOBILE_FALLBACK_TIERS: GbTier[] = [
  { gb: 25, monthlyPPG: 2, totalPrice: 50 },
  { gb: 50, monthlyPPG: 2, totalPrice: 100 },
  { gb: 100, monthlyPPG: 2, totalPrice: 200 },
  { gb: 200, monthlyPPG: 2, totalPrice: 400 },
  { gb: 500, monthlyPPG: 2, totalPrice: 1000 },
  { gb: 1000, monthlyPPG: 2, totalPrice: 2000 },
  { gb: 2000, monthlyPPG: 2, totalPrice: 4000 },
  { gb: 5000, monthlyPPG: 2, totalPrice: 10000 },
  { gb: 10000, monthlyPPG: 2, totalPrice: 20000 },
];

export const PROXIDIZE_RESIDENTIAL_FALLBACK_TIERS: GbTier[] = [
  { gb: 25, monthlyPPG: 1, totalPrice: 25 },
  { gb: 50, monthlyPPG: 1, totalPrice: 50 },
  { gb: 100, monthlyPPG: 1, totalPrice: 100 },
  { gb: 200, monthlyPPG: 1, totalPrice: 200 },
  { gb: 500, monthlyPPG: 1, totalPrice: 500 },
  { gb: 1000, monthlyPPG: 1, totalPrice: 1000 },
  { gb: 2000, monthlyPPG: 0.95, totalPrice: 1900 },
  { gb: 5000, monthlyPPG: 0.95, totalPrice: 4750 },
  { gb: 10000, monthlyPPG: 0.9, totalPrice: 9000 },
];

export interface DataImpulseCost {
  cost: number;
  perGb: number;
  tierLabel: string;
}

// Finds the highest published rate breakpoint the given volume has crossed,
// then prices the full volume at that rate — the same way DataImpulse's own
// packages work (their 1 TB package price divided by 1 TB equals the
// breakpoint rate exactly).
export function dataImpulseMonthlyCost(product: ProxyProduct, gb: number): DataImpulseCost {
  const tiers = product === "mobile" ? DATAIMPULSE_MOBILE_TIERS : DATAIMPULSE_RESIDENTIAL_TIERS;
  const tier = [...tiers].reverse().find((t) => gb >= t.minGb) ?? tiers[0];
  return {
    cost: Math.round(gb * tier.perGb * 100) / 100,
    perGb: tier.perGb,
    tierLabel: `${tier.label}, $${tier.perGb}/GB`,
  };
}
